import { Building2, Search } from 'lucide-react';
import { useState } from 'react';
import { useTrackCrmSearch } from '../hooks/useTrackCrmSearch';
import { useHubSpotCompanies } from '../services/dealCreation.api';
import type { DealCreationDraft } from '../utils/dealCreationDraft';
import { CrmNoMatch, CrmRecordRow, CrmSearchField, SelectedRecord } from './CrmRecordPicker';
import { DealField } from './DealField';
import { RecordModeToggle } from './RecordModeToggle';

interface CompanyStepProps {
  draft: DealCreationDraft;
  onChange: (patch: Partial<DealCreationDraft>) => void;
}

const COMPANY_ICON = <Building2 aria-hidden />;

/** Step 1: the company the deal is with — attached from the CRM, or created alongside it. */
export function CompanyStep({ draft, onChange }: CompanyStepProps) {
  const [term, setTerm] = useState('');
  const [searched, setSearched] = useState('');
  const { data, isFetching } = useHubSpotCompanies(searched, draft.companyMode === 'attach');
  const companies = data?.companies ?? [];
  useTrackCrmSearch({
    recordType: 'company',
    searched,
    isFetching,
    resultCount: companies.length,
  });
  // From the draft, not from the search: the picked company has to survive leaving the step and
  // coming back, when no search has been run yet.
  const selected = draft.companyId !== '' ? draft.attachedCompany : undefined;

  return (
    <div className="flex flex-col gap-4">
      <RecordModeToggle
        mode={draft.companyMode}
        onChange={(companyMode) => onChange({ companyMode })}
      />

      {draft.companyMode === 'attach' ? (
        selected ? (
          <SelectedRecord
            icon={COMPANY_ICON}
            title={selected.name}
            subtitle={[selected.city, selected.address].filter(Boolean).join(' · ')}
            clearLabel="Change company"
            onClear={() => onChange({ companyId: '', attachedCompany: undefined })}
          />
        ) : (
          <div className="flex flex-col gap-2.5">
            <CrmSearchField
              id="company-search"
              label="Company name"
              icon={<Search aria-hidden />}
              placeholder="Company name"
              value={term}
              onChange={setTerm}
              onSearch={() => setSearched(term.trim())}
            />

            {isFetching && <p className="text-sm text-primary-500">Searching the CRM…</p>}

            {!isFetching && searched !== '' && companies.length === 0 && (
              <CrmNoMatch>No match — try “Create new”.</CrmNoMatch>
            )}

            <div className="flex max-h-[220px] flex-col gap-0.5 overflow-y-auto">
              {!isFetching &&
                companies.map((company) => (
                  <CrmRecordRow
                    key={company.id}
                    icon={COMPANY_ICON}
                    title={company.name}
                    subtitle={[company.city, company.address].filter(Boolean).join(' · ')}
                    onSelect={() => onChange({ companyId: company.id, attachedCompany: company })}
                  />
                ))}
            </div>
          </div>
        )
      ) : (
        <div className="flex flex-col gap-3">
          <DealField
            id="company-name"
            label="Company name"
            required
            value={draft.company.name}
            onChange={(name) => onChange({ company: { ...draft.company, name } })}
          />
          <DealField
            id="company-street"
            label="Street address"
            value={draft.company.street ?? ''}
            onChange={(street) => onChange({ company: { ...draft.company, street } })}
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-x-3.5 sm:gap-y-0">
            <DealField
              id="company-postcode-row"
              label="Postal code"
              value={draft.company.postcode ?? ''}
              onChange={(postcode) => onChange({ company: { ...draft.company, postcode } })}
            />
            <DealField
              id="company-city-row"
              label="City"
              value={draft.company.city ?? ''}
              onChange={(city) => onChange({ company: { ...draft.company, city } })}
            />
            <DealField
              id="company-country"
              label="Country"
              value={draft.company.country ?? ''}
              onChange={(country) => onChange({ company: { ...draft.company, country } })}
            />
          </div>

          {/* The point starts on the location's and stays correctable: a company's registered seat
              is not always where the deal is. Both halves travel or neither does, so a cleared one
              leaves the company unplaced rather than at 0,0. */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-x-3.5 sm:gap-y-0">
            <DealField
              id="company-latitude"
              label="Latitude"
              value={draft.company.latitude?.toString() ?? ''}
              onChange={(value) =>
                onChange({ company: { ...draft.company, latitude: toCoordinate(value) } })
              }
            />
            <DealField
              id="company-longitude"
              label="Longitude"
              value={draft.company.longitude?.toString() ?? ''}
              onChange={(value) =>
                onChange({ company: { ...draft.company, longitude: toCoordinate(value) } })
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Read a typed coordinate half. Anything that is not a number reads as absent, which keeps a
 * half-typed value from being sent as a point.
 */
function toCoordinate(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}
